import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  Webhook, 
  AlertCircle, 
  CheckCircle, 
  RefreshCw, 
  Activity,
  Clock,
  XCircle,
  RotateCw,
  Database
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

interface WebhookEvent {
  id: string;
  action: string;
  resource_type: string;
  resource_id: string;
  details: any;
  created_at: string;
}

interface SubscriptionSyncStats {
  total_subscriptions: number;
  synced_count: number;
  error_count: number;
  errors: string[];
  synced_at: string;
}

export function WebhookMonitoringModule() {
  const [webhookEvents, setWebhookEvents] = useState<WebhookEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncStats, setSyncStats] = useState<SubscriptionSyncStats | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchWebhookEvents();
  }, []);

  const fetchWebhookEvents = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('audit_logs')
        .select('*')
        .ilike('action', 'webhook_%')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      setWebhookEvents(data || []);
    } catch (error) {
      console.error('Error fetching webhook events:', error);
      toast({
        title: "Fehler",
        description: "Webhook-Events konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const syncSubscriptions = async () => {
    try {
      setSyncing(true);
      
      const { data, error } = await supabase.functions.invoke('sync-subscriptions', {
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
      });

      if (error) throw error;

      setSyncStats(data);
      toast({
        title: "Synchronisation abgeschlossen",
        description: `${data.synced_count} Subscriptions synchronisiert, ${data.error_count} Fehler.`,
      });

      // Refresh webhook events to show sync activity
      fetchWebhookEvents();

    } catch (error) {
      console.error('Error syncing subscriptions:', error);
      toast({
        title: "Synchronisation fehlgeschlagen",
        description: error.message || "Unbekannter Fehler bei der Synchronisation.",
        variant: "destructive",
      });
    } finally {
      setSyncing(false);
    }
  };

  const getEventIcon = (action: string) => {
    if (action.includes('subscription')) return <Activity className="h-4 w-4" />;
    if (action.includes('payment')) return <CheckCircle className="h-4 w-4" />;
    if (action.includes('customer')) return <Database className="h-4 w-4" />;
    return <Webhook className="h-4 w-4" />;
  };

  const getEventBadge = (details: any) => {
    if (details.success === false) {
      return <Badge variant="destructive" className="ml-2">Fehler</Badge>;
    }
    if (details.success === true) {
      return <Badge variant="default" className="ml-2">Erfolg</Badge>;
    }
    return <Badge variant="secondary" className="ml-2">Verarbeitet</Badge>;
  };

  const formatEventDetails = (details: any) => {
    const safeDetails = { ...details };
    delete safeDetails.success;
    delete safeDetails.error;
    
    return Object.entries(safeDetails)
      .filter(([key, value]) => value !== null && value !== undefined)
      .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
      .join(', ');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Webhook className="h-5 w-5" />
          Webhook Monitoring & Sync
        </CardTitle>
        <CardDescription>
          Überwachung von Stripe Webhooks und Subscription-Synchronisation
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="events" className="space-y-4">
          <TabsList>
            <TabsTrigger value="events">Webhook Events</TabsTrigger>
            <TabsTrigger value="sync">Subscription Sync</TabsTrigger>
          </TabsList>

          <TabsContent value="events" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Letzte Webhook Events</h3>
              <Button 
                onClick={fetchWebhookEvents} 
                disabled={loading}
                variant="outline"
                size="sm"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Aktualisieren
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-muted-foreground">Lade Webhook Events...</p>
              </div>
            ) : webhookEvents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Webhook className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Keine Webhook Events gefunden</p>
              </div>
            ) : (
              <div className="space-y-2">
                {webhookEvents.map((event) => (
                  <Card key={event.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        {getEventIcon(event.action)}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {event.action.replace('webhook_', '').replace('_', '.')}
                            </span>
                            {getEventBadge(event.details)}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            Resource: {event.resource_id || 'N/A'}
                          </div>
                          {event.details.error && (
                            <div className="text-sm text-destructive mt-1">
                              Fehler: {event.details.error}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground mt-1">
                            {formatEventDetails(event.details)}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {format(new Date(event.created_at), 'dd.MM.yy HH:mm', { locale: de })}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="sync" className="space-y-4">
            <div className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Subscription Synchronisation</AlertTitle>
                <AlertDescription>
                  Synchronisiert alle Stripe Subscriptions mit der lokalen Datenbank. 
                  Dies sollte nur bei Problemen oder nach manuellen Änderungen in Stripe verwendet werden.
                </AlertDescription>
              </Alert>

              <Button 
                onClick={syncSubscriptions} 
                disabled={syncing}
                className="w-full"
              >
                {syncing ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Synchronisiere...
                  </>
                ) : (
                  <>
                    <RotateCw className="h-4 w-4 mr-2" />
                    Subscriptions synchronisieren
                  </>
                )}
              </Button>

              {syncStats && (
                <Card className="p-4">
                  <h4 className="font-semibold mb-3">Letzte Synchronisation</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {syncStats.total_subscriptions}
                      </div>
                      <div className="text-sm text-muted-foreground">Gesamt</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {syncStats.synced_count}
                      </div>
                      <div className="text-sm text-muted-foreground">Synchronisiert</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">
                        {syncStats.error_count}
                      </div>
                      <div className="text-sm text-muted-foreground">Fehler</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-medium">
                        {format(new Date(syncStats.synced_at), 'dd.MM.yy HH:mm', { locale: de })}
                      </div>
                      <div className="text-sm text-muted-foreground">Zeitpunkt</div>
                    </div>
                  </div>

                  {syncStats.errors.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <h5 className="font-medium text-destructive">Fehler:</h5>
                      {syncStats.errors.map((error, index) => (
                        <div key={index} className="text-sm text-muted-foreground bg-destructive/5 p-2 rounded">
                          {error}
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}